import { Modal } from '@fozito/common-ui';
import { Form, FormProps } from 'antd';
import { debounce, get, set, sum } from 'lodash';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import { IGiftCardPaymentValue, IUICashierFormValues } from 'modules/POS/Cashier/cashier.interface';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import useConfirmTipTicket from 'modules/POS/PaymentDetails/ConfirmTipTicket';
import { useTicketWrapper } from 'modules/POS/Receipts/context';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { IApiCashierPaymentBody } from 'modules/POS/services/types/payment';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import styled from 'styled-components';
import { parseDecimal } from 'utils/number';
import storage from 'utils/sessionStorage';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import { setPageLoading } from 'widgets/PageLoading';
import { MULTI_PAYMENT_TYPES, PAYMENT_FORM_NAMES } from './constants';
import CreditCardPaymentHandler, { useCreditCardPaymentHandlerRef } from './CreditCardPaymentHandler';
import { calcCashChance, calcRemaining, getMultiplePaymentInfo, IFormMultiPaymentValues, useCardFee, useCashIncentiveDiscount, usePointExchangeDollar } from './helpers';
import MultiPayForm, { useMultiPayFormRef } from './MultiPayForm';
import QRPaymentHandler, { useQRPaymentHandlerRef } from './QRPayment';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';
import toast from 'utils/toast';
const Container = styled(Form)``;
type Props = {
  discountSetting: IUICashierFormValues['cashIncentive'],
  enableCashIncentive: IUICashierFormValues['cashIncentiveDiscount'],
  enableCardFee: IUICashierFormValues['chargeFee'],
};
export type ModalMultiplePaymentRef = {
  open: (billId: string) => void;
  continueGiftCardPayWithMultiPay: (billId: string, val: IGiftCardPaymentValue) => void;
  continueLoyaltyPayWithMultiPay: (billId: string, val: {
    amount: number;
    point: number;
  }) => void;
};
export const useModalMultiplePaymentsRef = () => useRef<ModalMultiplePaymentRef>(null);
const ModalMultiplePayments = forwardRef<ModalMultiplePaymentRef, Props>(({ enableCardFee, enableCashIncentive, discountSetting }, ref) => {
  const [form] = Form.useForm();
  const [billId, setOpen] = useState('');
  const qrPaymentRef = useQRPaymentHandlerRef();
  const creditCardPaymentRef = useCreditCardPaymentHandlerRef();
  const multiFormRef = useMultiPayFormRef();
  const [completedTicketModalRef, completedTicketModalContext] = useConfirmTipTicket();

  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
    form.resetFields();
  };

  const onCreditCardCancel = () => {
    socketContext.backToCheckIn();
    setOpen('');
    form.resetFields();
  };

  const ticketContext = useTicketWrapper();
  const socketContext = useSocketContext();
  const cashCheckDiscount = useCashIncentiveDiscount(discountSetting);
  const cardFeeHook = useCardFee();
  const exchangeLoyalty = usePointExchangeDollar();

  const fetchDetail = async (_payload: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(_payload);
      const detail = res?.data?.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(res.data.data);
        form.resetFields(PAYMENT_FORM_NAMES.map(o => `isValid_${o}`));
        form.setFieldValue('detail', detail);
        form.setFieldValue('amount', detail?.total || 0);
        form.setFieldValue('enableCardFee', enableCardFee);
        form.setFieldValue('enableIncentive', enableCashIncentive);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: ModalMultiplePaymentRef['open'] = async (_billId) => {
    socketContext.switchCusPayment(_billId);
    completedTicketModalRef.current?.close();
    try {
      form.resetFields();
      const valid = await fetchDetail(_billId);
      if (!valid) return;
      setOpen(_billId);
      setTimeout(() => { multiFormRef.current?.init(); }, 200);
    } catch (error) {
    } finally { }
  };

  const onCompleted = async (_billId: string, ignorePrint?: boolean) => {
    if (!_billId) return;
    cashierService.actions.saveCashierMessage(null);
    socketContext.alertPaymentSuccess(_billId);
    const callback = () => {
      onClose();
      cashierCtrl.generateNewTicketAfterPayment();
    };
    if (!completedTicketModalRef.current) {
      callback();
      if (ignorePrint) {
        ticketContext.doPrintNoReceipt(billId, true);
        return;
      }
      ticketContext.completedTicket(billId, true);
      return;
    }
    completedTicketModalRef.current.open(billId, callback, ignorePrint);
  };

  const onCCCompleted = (ignorePrint?: boolean) => {
    const _billId = form.getFieldValue(['detail', 'billId']);
    if (!_billId) return;
    socketContext.alertPaymentSuccess(_billId);
    cashierService.actions.saveCashierMessage(null);
    creditCardPaymentRef.current?.closeSignature();
    const callback = () => {
      setOpen('');
      cashierCtrl.generateNewTicketAfterPayment();
    };
    if (!completedTicketModalRef.current) {
      callback();
      if (ignorePrint) {
        ticketContext.doPrintNoReceipt(billId, true);
        return;
      }
      ticketContext.completedTicket(billId, true);
      return;
    }
    completedTicketModalRef.current.open(billId, callback, ignorePrint);
  };

  const doQRPayment = async (values: IFormMultiPaymentValues, method: string, amount: number) => {
    const billId = values.detail.billId;
    if (!billId) return;
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.QR_MULTIPLE_PAYMENT_REQUEST_VISIBLE,
      data: { type: method, amount },
    };
    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
    qrPaymentRef.current?.requestQRPayment(method, amount);
  };

  const completeWithCreditCard = async (values: IFormMultiPaymentValues) => {
    if (!values.detail?.billId) return;
    // TODO:
    // const cashDiscount = values.cashIncentiveValue + values.checkIncentiveValue;
    // if (values.enableIncentive && !!cashDiscount) {
    //   await updateDiscountCashIncentive(values);
    // }

    const creditValue = values.CREDIT_CARD || 0;
    const totalCC = creditValue + (values.cardFee || 0);
    socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
      billId: values.detail?.billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.TIP_BEFORE_PAYMENT_SEND_TIP_VALUE,
      data: { tip: values.tip },
    });
    creditCardPaymentRef.current?.openCCLoading(totalCC);
    const payments = getMultiplePaymentInfo(values);
    const body: IApiCashierPaymentBody = {
      billId: values.detail.billId,
      payments,
    };
    try {
      const res: IResponseData<true> = await cashierApis.paymentTicket(body);
      if (res?.data?.data) {
        creditCardPaymentRef.current?.requestSignature();
        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId: values.detail?.billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.TIP_BEFORE_PAYMENT_REQUEST_SIGNATURE
        });
      }
    } catch (error) {
      const msg = get(error, ['response', 'data', 'message'], 'Fail');
      creditCardPaymentRef.current?.closeCCLoading();
      toast.error(msg);
    }
  };

  const completeWithCash = async (values: IFormMultiPaymentValues) => {
    const payments = getMultiplePaymentInfo(values);
    const body: IApiCashierPaymentBody = {
      billId: values.detail.billId,
      payments,
    };

    const res: IResponseData<true> = await cashierApis.paymentTicket(body);
    if (res?.data?.data) {
      onCompleted(values.detail.billId, values.ignorePrint);
    } else {
      throw 'fail';
    }
  };

  const doPayment = async (values: IFormMultiPaymentValues) => {
    if (values.VENMO && !values.isValid_VENMO) {
      return await doQRPayment(values, MULTI_PAYMENT_TYPES.VENMO, values.VENMO);
    }
    if (values.PAYPAL && !values.isValid_PAYPAL) {
      return await doQRPayment(values, MULTI_PAYMENT_TYPES.PAYPAL, values.PAYPAL);
    }
    if (values.CASH_APPS && !values.isValid_CASH_APPS) {
      return await doQRPayment(values, MULTI_PAYMENT_TYPES.CASH_APPS, values.CASH_APPS);
    }
    if (values.ZELLE && !values.isValid_ZELLE) {
      return await doQRPayment(values, MULTI_PAYMENT_TYPES.ZELLE, values.ZELLE);
    }
    if (values.CREDIT_CARD) {
      return await completeWithCreditCard(values);
    }
    await completeWithCash(values);
  };

  const handleFinish = async (values: IFormMultiPaymentValues) => {
    if (!values.CREDIT_CARD) {
      setPageLoading(true);
    }

    try {
      await doPayment(values);
    } catch (error) {
      form.resetFields(PAYMENT_FORM_NAMES.map(o => `isValid_${o}`));
    } finally {
      creditCardPaymentRef.current?.closeCCLoading();
      setPageLoading(false);
    }
  };

  const sendingData = (type: string, data?: any) => {
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId: billId || '',
      actor: TypePaymentActors.CASHIER,
      data,
    };
    switch (type) {
      case TypePaymentPassData.PASS_DATA: {
        set(body, 'action', TypePaymentPassData.PASS_DATA);
        break;
      }
      default:
        break;
    }

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };

  const debounceValuesChange: FormProps['onValuesChange'] = debounce(() => {
    const values: IFormMultiPaymentValues = form.getFieldsValue();
    const billId = values.detail?.billId || '';
    const detail = values.detail;
    const remaining = calcRemaining(values);
    if (!billId) return;
    sendingData(TypePaymentPassData.PASS_DATA, {
      cash: values.CASH || 0,
      remaining: remaining || 0,
      chanceCash: values.chance ? -values.chance : 0,
      loyalty: values.LOYALTY_POINT || 0,
      giftCard: values.GIFT_CARD || 0,
      card: values.CREDIT_CARD || 0,
      multiPay: 1,
      checkOthers: sum([values.CHECK || 0, values.CASH_APPS || 0, values.VENMO || 0, values.PAYPAL || 0, values.ZELLE || 0]),
      check: values.CHECK || 0,
      cashApp: values.CASH_APPS || 0,
      venmo: values.VENMO || 0,
      paypal: values.PAYPAL || 0,
      zelle: values.ZELLE || 0,

      totalValue: detail?.total,
      subTotal: detail?.subTotal || 0,
      cardFee: values.cardFee || 0,
      saleTax: detail?.saleTax || 0,
      usedTax: detail?.useTax || 0,
      discount: (detail?.discount || 0) + (detail?.discountItem || 0) + (values.cashIncentiveValue || 0) + (values.checkIncentiveValue || 0),
    } as ICustomerPaymentDetail);
  }, 1000);

  const onValuesChange: FormProps['onValuesChange'] = (changedValues, values: IFormMultiPaymentValues) => {
    const incentivePercent = cashCheckDiscount.getIncentivePercent();
    const cashIncentiveValue = cashCheckDiscount.calcAmountDiscount(values.CASH, values);
    const chance = parseDecimal(calcCashChance(values, cashIncentiveValue));
    const checkIncentiveValue = cashCheckDiscount.calcAmountDiscount(values.CHECK, values);

    const cardFee = cardFeeHook.getAmountCardFee(values);
    const cardFeeLabel = cardFeeHook.getLabelCardFee(cardFee);

    const availablePoint = values.detail?.customerPoint || 0;
    const availablePointExchange = parseDecimal(exchangeLoyalty.exchangeDollar(availablePoint));
    const redeemPoint = parseDecimal(exchangeLoyalty.exchangePoint(values.LOYALTY_POINT || 0));

    const balancePoint = availablePoint - redeemPoint;
    const balancePointExchange = parseDecimal(exchangeLoyalty.exchangeDollar(balancePoint));

    form.setFieldsValue({
      incentivePercent,
      cashIncentiveValue,
      checkIncentiveValue,
      chance,

      cardFee,
      cardFeeLabel,

      availablePoint,
      availablePointExchange,
      redeemPoint,
      balancePoint,
      balancePointExchange,
    });

    form.validateFields(['amountValid']);

    debounceValuesChange(changedValues, values);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    async continueGiftCardPayWithMultiPay(_billId, val: IGiftCardPaymentValue) {
      socketContext.switchCusPayment(_billId);
      completedTicketModalRef.current?.close();
      setPageLoading(true);
      try {
        form.resetFields();
        const valid = await fetchDetail(_billId);
        if (!valid) return;
        setOpen(_billId);
        setTimeout(() => { multiFormRef.current?.init(); }, 200);
        const { amount = 0, usedAmount = 0, id } = val.data || {};
        form.setFieldsValue({
          giftCardCode: val.data?.giftCode,
          giftCardId: id,
          giftCardAvailable: amount - usedAmount,
          GIFT_CARD: val.amount,
        });
      } catch (error) {
      } finally {
        setPageLoading(false);
      }
    },
    async continueLoyaltyPayWithMultiPay(_billId, val) {
      socketContext.switchCusPayment(_billId);
      completedTicketModalRef.current?.close();
      setPageLoading(true);
      try {
        form.resetFields();
        const valid = await fetchDetail(_billId);
        if (!valid) return;
        setOpen(_billId);
        setTimeout(() => { multiFormRef.current?.init(); }, 200);
        form.setFieldValue(MULTI_PAYMENT_TYPES.POINT, val.amount);
      } catch (error) {
      } finally {
        setPageLoading(false);
      }
    },
  }));
  if (!billId) return null;
  return (
    <>
      <Modal title='' open onClose={onClose} width={'auto'} styles={{ header: { display: 'none' }, body: { maxHeight: 'unset' } }}>
        <Container<any> form={form} onFinish={handleFinish} onValuesChange={onValuesChange}>
          <MultiPayForm ref={multiFormRef} onClose={onClose} />
          <Form.Item name={'detail'} noStyle />
          <Form.Item name={'ignorePrint'} noStyle />
          <Form.Item name={'chance'} noStyle />
          <Form.Item name={'cardFee'} noStyle />
          <Form.Item name={'cardFeeLabel'} noStyle />
          <Form.Item name={'giftCardId'} noStyle />
          <Form.Item name={'giftCardCode'} noStyle />
          <Form.Item name={'giftCardAvailable'} noStyle />
          <Form.Item name={'cashIncentiveValue'} noStyle />
          <Form.Item name={'checkIncentiveValue'} noStyle />
          <Form.Item name={'incentivePercent'} noStyle />
          <Form.Item name={'availablePoint'} noStyle />
          <Form.Item name={'availablePointExchange'} noStyle />
          <Form.Item name={'redeemPoint'} noStyle />
          <Form.Item name={'balancePoint'} noStyle />
          <Form.Item name={'balancePointExchange'} noStyle />
          <Form.Item name={'giftCreditCode'} noStyle />
          {PAYMENT_FORM_NAMES.map(o => <Form.Item key={o} noStyle name={`isValid_${o}`} />)}
          <QRPaymentHandler ref={qrPaymentRef} sendingData={sendingData} />
          <Form.Item name={'detail'} noStyle >
            <CreditCardPaymentHandler
              ref={creditCardPaymentRef}
              billId={billId}
              onCCCompleted={onCCCompleted}
              onCreditCardCancel={onCreditCardCancel}
            />
          </Form.Item>
        </Container>
      </Modal>
      {completedTicketModalContext}
    </>
  );
});
ModalMultiplePayments.displayName = 'ModalMultiplePayments';
export default ModalMultiplePayments;
